// Analytics dispatch — push to dataLayer AND POST to sGTM directly.
//
// The dataLayer push is the long-term production path (consumed by
// CC's existing Web Container with "Send to server container" enabled
// on booking-related tags).
//
// The direct sGTM POST is a temporary transport so we can validate the
// end-to-end flow against the 2x4 test GA4 property today, before the
// Web Container has been wired. Once Web Container forwarding is live,
// this transport can be removed.

import type { EventParams } from './events';
import { ONCE_PER_SESSION, SCHEMA_VERSION } from './events';
import { maybeEmitLegacy } from './legacy-dispatch';
import { maybeEmitHostPixels } from './host-dispatch';
import { getAttribution } from './attribution';

let clientId: string | null = null;
let sessionId: string | null = null;
let webSubmissionId: string | null = null;
let persistentUserId: string | null = null;
const firedThisSession = new Set<string>();

// Per-cid engagement tracking. GA4 considers a session "engaged" when
// engagement_time_msec (sent as `_et` on each MP hit) accumulates to >=
// 10s, or when a key event fires, or when 2+ page_views happen. Our
// earlier behavior of sending `_et=100` on every event meant 11 events
// only contributed 1.1s of engagement — far below the threshold —
// which is part of why widget-only sessions show as 0s engagement in
// GA4. We now compute the real elapsed milliseconds since the last
// emit (capped at 30s, gtag's per-event cap) so the actual time the
// user spent in the booking flow accumulates correctly.
let lastEventTimeMs = 0;
let widgetVisibilityListenerAttached = false;

// Session-cookie race guard. On cold landings, gtag.js may not have
// written the `_ga_<id>` cookie yet by the time the user clicks a
// trigger above the fold. If the widget fires its first event in
// that window, it generates a fallback sid (timestamp-based) that
// doesn't match the sid gtag writes ~100-800ms later — splitting the
// user into two GA4 sessions: one anchored on our fallback sid
// (carrying our attribution payload), one anchored on gtag's sid
// (carrying the conversion). The attribution lives on the wrong
// session and conversions arrive in GA4 as Unassigned.
//
// Mitigation: on the first postToSgtm call, briefly poll for the
// cookie before sending. If it appears, all events go out joined to
// the host session. If the poll times out (truly cookie-less visit —
// privacy mode, consent withheld, gtag broken), we send anyway with
// the fallback so we don't drop attribution entirely.
// Wait up to 3s for gtag's cookie. 1s was the original cap but real-world
// slow-load cases (mobile networks, cold cache, ad-blocker first-paint
// delays) routinely pushed gtag past 1.5-2s, causing the fallback sid to
// fire and fragmenting the session. 3s covers the long tail without
// blocking widget interaction in the common fast-gtag case (cookie
// usually appears under 800ms, so this only extends the wait for the
// minority where it wasn't going to work in 1s anyway).
const COOKIE_WAIT_TIMEOUT_MS = 3000;
const COOKIE_POLL_INTERVAL_MS = 50;
let cookieWaitStarted = false;
let cookieWaitCleared = false;
let pendingEmits: Array<() => void> = [];

function nextEngagementMs(): number {
  const now = Date.now();
  if (lastEventTimeMs === 0) {
    lastEventTimeMs = now;
    return 100;
  }
  const elapsed = now - lastEventTimeMs;
  lastEventTimeMs = now;
  if (elapsed < 0) return 100;
  return Math.min(elapsed, 30_000);
}

/**
 * Attaches a one-time visibilitychange listener that fires a final
 * `user_engagement` event when the tab becomes hidden. This mirrors
 * what gtag.js does natively — flushing accumulated engagement on
 * visibility loss so sessions where the user opens the widget, spends
 * 90 seconds reading it, then closes the tab still count as engaged.
 *
 * Safe to call multiple times; only the first call attaches.
 */
function ensureEngagementFlushListener(): void {
  if (widgetVisibilityListenerAttached) return;
  if (typeof document === 'undefined') return;
  widgetVisibilityListenerAttached = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'hidden') return;
    // Don't reset lastEventTimeMs here — if the tab becomes visible
    // again the elapsed time would balloon. Send what's accumulated.
    postToSgtm('user_engagement', {});
  });
}

/**
 * Stable session-scoped client identifier in the GA4 cookie format.
 * No actual cookie — derived in-memory only.
 */
function getClientId(): string {
  // Always try the cookie FIRST on every call — same reasoning as
  // getSessionId. cid changes less often than sid (the `_ga` cookie's
  // client_id portion is stable across sessions), so this matters less
  // here, but we keep the pattern symmetric so a stale cached cid never
  // pollutes events emitted by a long-lived tab.
  const fromCookie = readChannelMixIdFromGaCookie();
  if (fromCookie) return fromCookie;
  if (clientId) return clientId;
  const rand = Math.floor(Math.random() * 1e10);
  clientId = `${rand}.${Math.floor(Date.now() / 1000)}`;
  return clientId;
}

function readSessionIdFromGaSessionCookie(measurementId: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  // GA4 has two on-the-wire formats for `_ga_<id>` depending on rollout:
  //   Newer (current on clubchampion.com): `GS2.1.s<session_id>$<key>$<value>...`
  //     where the third dot-segment is `s<digits>` followed by `$`-separated kv pairs.
  //   Older: `GS1.1.<session_id>.<session_count>.<engaged>.<last_active>.<first_active>.<count>.0`
  //     where the third dot-segment IS the session id as a clean digit run.
  // We try the new format first because that's what live customers have.
  const cookieName = `_ga_${measurementId.replace(/^G-/, '')}`;
  const escaped = cookieName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${escaped}=([^;]+)`));
  if (!match) return undefined;
  const value = decodeURIComponent(match[1]);
  const newFormat = value.match(/^GS\d+\.\d+\.s(\d+)(?:\$|\.|$)/);
  if (newFormat) return newFormat[1];
  const parts = value.split('.');
  if (parts.length >= 3 && /^\d+$/.test(parts[2])) return parts[2];
  return undefined;
}

function getSessionId(): string {
  // Always try the cookie FIRST on every call — never cache the result.
  // GA4 rolls a new session_id after 30 min of inactivity, and a long-lived
  // tab (or one restored from bfcache) can outlive multiple sessions.
  // Caching meant the widget could send a stale session_id from an earlier
  // session even after gtag refreshed the cookie. Re-reading the cookie
  // every time keeps us joined to whichever session is currently active.
  if (typeof window !== 'undefined') {
    const cfg = window.CC_WIDGET_CONFIG ?? {};
    if (cfg.ga4MeasurementId) {
      const fromCookie = readSessionIdFromGaSessionCookie(cfg.ga4MeasurementId);
      if (fromCookie) return fromCookie;
    }
  }
  // Fall back to a generated id ONLY when there's no cookie at all
  // (rare: privacy-mode visit before gtag fires). Memoize so two events
  // in the same uncached run share an id rather than each picking a
  // different timestamp.
  if (sessionId) return sessionId;
  sessionId = String(Math.floor(Date.now() / 1000));
  return sessionId;
}

/**
 * Per-session widget submission ID. Format: `WS-<random>`.
 * Generated once on first emit, reused for the rest of the session.
 * CC prepends "WS-" when storing in Salesforce per Dan's May 29 contract;
 * we generate the same prefix here so the value is portable end-to-end.
 */
export function getWebSubmissionId(): string {
  if (webSubmissionId) return webSubmissionId;
  const rand = Math.random().toString(36).slice(2, 12).toUpperCase();
  webSubmissionId = `WS-${rand}`;
  return webSubmissionId;
}

let growthbookVisitorId: string | null = null;

const GROWTHBOOK_VISITOR_ID_STORAGE_KEY = 'cc_widget_gb_visitor_id';

/**
 * Stable, cross-visit visitor id used ONLY as the GrowthBook bucketing
 * attribute (`attributes.id` in main.tsx). Deliberately distinct from
 * getWebSubmissionId() and marketing.ts's submission id — those identify a
 * single booking attempt and are intentionally session-scoped; this
 * identifies a returning browser so experiment variant assignment stays
 * consistent across reloads and later visits. A session-scoped id would
 * re-bucket a returning user and corrupt the experiment read.
 *
 * Persisted in localStorage (not sessionStorage) so it survives tab close.
 * Falls back to an in-memory-only id when storage is blocked (private mode,
 * quota errors) or on return visits after the browser has cleared it —
 * never throws into widget boot.
 */
export function getGrowthBookVisitorId(): string {
  if (growthbookVisitorId) return growthbookVisitorId;

  if (typeof window !== 'undefined') {
    try {
      const existing = window.localStorage.getItem(GROWTHBOOK_VISITOR_ID_STORAGE_KEY);
      if (existing) {
        growthbookVisitorId = existing;
        return growthbookVisitorId;
      }
    } catch {
      // localStorage unavailable — fall through to in-memory generation.
    }
  }

  growthbookVisitorId =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `gb-${Math.random().toString(36).slice(2, 12)}${Date.now().toString(36)}`;

  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(GROWTHBOOK_VISITOR_ID_STORAGE_KEY, growthbookVisitorId);
    } catch {
      // Storage write failed — in-memory value still serves this page.
    }
  }

  return growthbookVisitorId;
}

/** Test-only reset. Not used in production. */
export function _resetGrowthBookVisitorId(): void {
  growthbookVisitorId = null;
}

/**
 * Set the persistent user_id (= Salesforce Account ID) once we have it
 * from the booking response. Subsequent events automatically include it.
 */
export function setAnalyticsUserId(id: string): void {
  if (id) persistentUserId = id;
}

/**
 * Read the ChannelMix client ID from the host page, if present. The
 * existing CC site uses a {{ChannelMix Client ID}} dataLayer variable;
 * on the staging host we don't have ChannelMix loaded, so this returns
 * undefined and the field is omitted.
 */
function getChannelMixClientId(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  // Highest precedence: host page set it explicitly via the widget config.
  const fromConfig = (window.CC_WIDGET_CONFIG ?? {}).channelmixClientId;
  if (typeof fromConfig === 'string' && fromConfig.length > 0) return fromConfig;
  // Secondary: a legacy global some pages exposed during the manual cutover.
  const fromGlobal = (window as unknown as { channelmix_client_id?: string }).channelmix_client_id;
  if (typeof fromGlobal === 'string' && fromGlobal.length > 0) return fromGlobal;
  // Fallback per Dan's 2026-06-10 sGTM setup doc: derive from the `_ga`
  // cookie that the existing ChannelMix script drops site-wide. Formula:
  // `split('.').slice(-2).join('.')`. A cookie value of
  // `GA1.1.1689584320.1652934820` yields the canonical GA Client ID
  // `1689584320.1652934820`. ChannelMix attribution lands on every event
  // without any host-page-side wiring.
  return readChannelMixIdFromGaCookie();
}

function readChannelMixIdFromGaCookie(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  // Multiple `_ga=` cookies can coexist on the host (different domains/paths,
  // different trackers — clubchampion.com had a UUID-format `_ga` and a
  // numeric-format `_ga` side by side). The browser concatenates them all
  // into document.cookie; matching the first one with a regex can pick the
  // wrong one. Iterate every match and return the first that parses as the
  // canonical GA4 numeric Client ID (`<id>.<timestamp>`).
  for (const c of document.cookie.split(/;\s*/)) {
    if (!c.startsWith('_ga=')) continue;
    const value = decodeURIComponent(c.slice('_ga='.length));
    const parts = value.split('.');
    if (parts.length < 2) continue;
    const derived = parts.slice(-2).join('.');
    if (/^\d+\.\d+$/.test(derived)) return derived;
  }
  return undefined;
}

/**
 * Push to window.dataLayer. CC's Web Container picks up these events
 * once Michael wires the triggers.
 */
function pushToDataLayer(payload: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
}

/**
 * Builds the `dl` (document location) value for an MP hit. GA4 derives
 * session source by parsing `dl` for `gclid` and `utm_*` query params.
 * On most widget hits `window.location.href` is the booking page URL
 * with the landing query string already stripped by the host site's
 * SPA navigation, so the URL alone gives GA4 nothing to attribute to.
 *
 * We re-inject the persisted attribution captured at first landing
 * (URL params, sessionStorage, or `_gcl_aw` cookie fallback) so GA4
 * has the values regardless of the page the widget is mounted on.
 * Existing params on the current URL win — if the user is somehow on a
 * page that still carries fresh attribution we don't overwrite it.
 */
function buildDocumentLocation(): string {
  if (typeof window === 'undefined') return '';
  const href = window.location.href;
  try {
    const url = new URL(href);
    const attribution = getAttribution();
    const inject = (key: string, value: string | undefined): void => {
      if (!value) return;
      if (url.searchParams.has(key)) return;
      url.searchParams.set(key, value);
    };
    inject('utm_source', attribution.utm_source);
    inject('utm_medium', attribution.utm_medium);
    inject('utm_campaign', attribution.utm_campaign);
    inject('utm_term', attribution.utm_term);
    inject('utm_content', attribution.utm_content);
    inject('gclid', attribution.gclid);
    // gbraid + wbraid: iOS-equivalent ad-click identifiers. GA4 parses
    // them off `dl` the same way as gclid for Google Ads attribution.
    // Forwarding them is critical on iOS Safari, where gclid is more
    // likely to be stripped by ITP.
    inject('gbraid', attribution.gbraid);
    inject('wbraid', attribution.wbraid);
    return url.toString();
  } catch {
    return href;
  }
}

/**
 * Direct POST to sGTM. Mirrors the shape of a GA4 collect request so the
 * GA4 Client in the server container parses it without a custom template.
 *
 * Exported so the legacy-dispatch sidecar can use the same transport
 * (sharing client/session IDs and config) when emitting the legacy
 * `booking` schema alongside the new schema.
 *
 * Fire and forget — analytics failures must never block the booking flow.
 */
export function postToSgtm(event: string, params: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;

  // A defined-but-sparse CC_WIDGET_CONFIG (e.g. only `embedSource`, the common
  // case for the staging standalone embed) leaves sgtmUrl / ga4MeasurementId
  // undefined. That just means the sGTM transport isn't configured — skip the
  // emit cleanly. This MUST be a silent no-op, never a throw: postToSgtm runs
  // on the widget mount / engage path via fireWidgetPageView, so throwing here
  // would abort the render.
  // Host CC_WIDGET_CONFIG wins; fall back to build-time baked values
  // (VITE_CC_DEFAULT_*) so a config-free standalone bundle — e.g. the prod /v2
  // embed the client drops in a bare div — still emits GA4 without host config.
  const cfg = window.CC_WIDGET_CONFIG ?? {};
  const sgtmUrl = cfg.sgtmUrl ?? import.meta.env.VITE_CC_DEFAULT_SGTM_URL;
  const measurementId = cfg.ga4MeasurementId ?? import.meta.env.VITE_CC_DEFAULT_GA4_ID;
  if (!sgtmUrl || !measurementId) return;

  // Defense in depth: everything below reads cookies, builds URLs, and calls
  // into helpers. None of it should throw, but if any ever does (malformed
  // config value, hostile document.cookie, a future regression), analytics
  // must fail safe rather than break the booking flow or the widget mount.
  try {
    // Attach the visibility-change listener early — we want flush-on-hide
    // to work even if the user closes the tab during the cookie-wait window.
    ensureEngagementFlushListener();

    // If gtag hasn't written the `_ga_<id>` cookie yet, buffer this emit
    // until the cookie appears (or the timeout fires). This avoids the
    // double-session race described in the cookie-wait constants above.
    if (!cookieWaitCleared) {
      if (readSessionIdFromGaSessionCookie(measurementId)) {
        cookieWaitCleared = true;
      } else {
        pendingEmits.push(() => sendToSgtm(sgtmUrl, measurementId, event, params));
        startCookieWait(sgtmUrl, measurementId);
        return;
      }
    }

    sendToSgtm(sgtmUrl, measurementId, event, params);
  } catch {
    /* analytics never blocks the booking flow or the widget mount */
  }
}

function startCookieWait(sgtmUrl: string, measurementId: string): void {
  if (cookieWaitStarted) return;
  cookieWaitStarted = true;
  const start = Date.now();
  const tick = (): void => {
    if (cookieWaitCleared) return;
    const hasCookie = !!readSessionIdFromGaSessionCookie(measurementId);
    const elapsed = Date.now() - start;
    if (hasCookie || elapsed >= COOKIE_WAIT_TIMEOUT_MS) {
      cookieWaitCleared = true;
      // Timed out without gtag's cookie — we're about to drain buffered
      // emits using our fallback sid. Without a `session_start` event
      // anchoring that fallback sid, GA4 reconstructs the session from
      // event-only data with no source/medium and lands the conversion
      // in (not set)/(not set) Unassigned. Discovered 2026-06-25: ~8.5%
      // of widget bookings on Jun 23 had no matching session_start in
      // BQ, which traced to this code path on cold-load / ad-blocker /
      // slow-gtag visits where the cookie never showed up in 3s.
      //
      // Fire a synthetic session_start first so GA4 has an anchor that
      // carries our attribution params. We only do this when the cookie
      // is missing — if gtag wrote the cookie, gtag already fired its
      // own session_start under the cookie's sid and a synthetic one
      // here would be a duplicate.
      //
      // The `cookie_wait_timeout: true` event param marks this session
      // as a fix-path session in BQ so we can:
      //   1. Validate the fix is working — count synthetic session_starts
      //      over time and confirm orphan count drops to ~zero.
      //   2. Track ongoing cookie-timeout failure rate as a signal of
      //      ad-blocker prevalence / gtag slowness on the host page.
      //   3. Distinguish synthetic anchors from gtag's real session_starts
      //      in attribution analysis. Query:
      //        SELECT COUNT(*) FROM events_*
      //        WHERE event_name='session_start'
      //          AND (SELECT value.string_value FROM UNNEST(event_params)
      //               WHERE key='cookie_wait_timeout') = 'true';
      if (!hasCookie) {
        sendToSgtm(sgtmUrl, measurementId, 'session_start', {
          cookie_wait_timeout: true,
        });
      }
      const drained = pendingEmits;
      pendingEmits = [];
      for (const emit of drained) {
        try {
          emit();
        } catch {
          // Never let one buffered emit's failure block the rest.
        }
      }
      return;
    }
    setTimeout(tick, COOKIE_POLL_INTERVAL_MS);
  };
  setTimeout(tick, COOKIE_POLL_INTERVAL_MS);
}

/**
 * The collect URL's query string, with spaces as %20.
 *
 * URLSearchParams.toString() is form encoding, which writes a space as "+".
 * gtag.js writes it as %20, and sGTM's GA4 client decodes only the percent
 * form, so a studio called "Mile End" landed in GA4 as "Mile+End" from this
 * transport while every other property (and the US before this transport)
 * showed "Mile End". Reporting parity across markets wants the gtag form.
 * A literal "+" in a value is already %2B here, so the swap is safe.
 */
export function collectQueryString(query: URLSearchParams): string {
  return query.toString().replace(/\+/g, '%20');
}

function sendToSgtm(
  sgtmUrl: string,
  measurementId: string,
  event: string,
  params: Record<string, unknown>,
): void {
  const cid = getClientId();
  const sid = getSessionId();
  const engagementMs = nextEngagementMs();
  const query = new URLSearchParams({
    v: '2',
    tid: measurementId,
    cid,
    _p: String(Date.now()),
    en: event,
    dl: buildDocumentLocation(),
    dr: typeof document !== 'undefined' ? document.referrer : '',
    dt: document.title,
    _et: String(engagementMs),
    sid,
    seg: '1',
  });

  // Session traffic-source assignment via top-level MP params.
  //
  // GA4 does NOT reliably derive session_traffic_source_last_click from `dl`
  // query params on Measurement Protocol hits, even when the URL clearly
  // contains utm_*. The reliable path is to send the same values as
  // top-level MP params (cs/cm/cn/cc/ck/gclid) — those map directly to the
  // session-scoped attribution fields GA4 channel grouping uses.
  //
  // Without this, sessions whose only signal is the widget's synthesized
  // `(direct)/(none)` fallback land in `(not set)/(not set)` → Unassigned
  // instead of Direct, because GA4 sees `dl=...%28direct%29...` and doesn't
  // recognize the URL-encoded literal.
  //
  // Distinct from the earlier event-param spread that was removed (see
  // emitAnalytics comment, ~line 498): event-level `utm_source` overrides
  // each event's traffic source and forced (not set) on direct traffic. The
  // top-level `cs/cm/cn` form is session-scoped and doesn't have that
  // problem — empty values are skipped via the truthy guard.
  const attr = getAttribution();
  if (attr.utm_source)   query.set('cs', attr.utm_source);
  if (attr.utm_medium)   query.set('cm', attr.utm_medium);
  if (attr.utm_campaign) query.set('cn', attr.utm_campaign);
  if (attr.utm_content)  query.set('cc', attr.utm_content);
  if (attr.utm_term)     query.set('ck', attr.utm_term);
  if (attr.gclid)        query.set('gclid', attr.gclid);
  if (attr.gbraid)       query.set('gbraid', attr.gbraid);
  if (attr.wbraid)       query.set('wbraid', attr.wbraid);

  // Top-level fields (NOT under ep.) per GA4 Measurement Protocol.
  // user_id → uid, channelmix_client_id → up.channelmix_client_id (user-scoped)
  const topLevelMap: Record<string, string> = {
    user_id: 'uid',
  };
  const userPropertyKeys = new Set(['channelmix_client_id']);

  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    if (k in topLevelMap && topLevelMap[k]) {
      query.set(topLevelMap[k] as string, String(v));
      continue;
    }
    if (userPropertyKeys.has(k)) {
      query.set(`up.${k}`, String(v));
      continue;
    }
    if (Array.isArray(v) || typeof v === 'object') {
      query.set(`ep.${k}`, JSON.stringify(v));
    } else if (typeof v === 'number') {
      // GA4 Measurement Protocol: epn.<name> preserves numeric type on the
      // wire. ep.<name> stringifies. Derived-event rules like `step equals 3`
      // are numeric comparisons in GA4 — sending ep.step=3 (string) silently
      // fails to match. Required for legacy step-named key events
      // (submitted_booking_website, fitting_selection_salesforce, etc.).
      query.set(`epn.${k}`, String(v));
    } else {
      query.set(`ep.${k}`, String(v));
    }
  }

  // GA4 collect: the params live in the URL, sGTM's GA4 client parses them
  // off the query string. We POST a synthetic body so the beacon survives
  // the page-unload window even when the browser refuses query-string-only
  // beacons under memory pressure.
  const url = `${sgtmUrl.replace(/\/$/, '')}/g/collect?${collectQueryString(query)}`;
  const body = new Blob([''], { type: 'text/plain' });

  // navigator.sendBeacon can return false (queue full, payload too large,
  // quota exhausted) — modern Chrome and Safari throttle aggressively when
  // many beacons fire in quick succession, which happens at the conversion
  // moment because book_flow_confirm_a_number dual-fires with legacy step=5 and is
  // immediately followed by channelmix_conversion. When that throttle hits
  // we lose the event silently unless we fall through to fetch+keepalive,
  // which has a separate queue managed by the network stack.
  let queued = false;
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      queued = navigator.sendBeacon(url, body);
    }
  } catch {
    queued = false;
  }
  if (!queued) {
    try {
      void fetch(url, {
        method: 'POST',
        body,
        keepalive: true,
        credentials: 'omit',
        // Avoid CORS preflight: text/plain is a safelisted Content-Type
        headers: { 'Content-Type': 'text/plain' },
      });
    } catch {
      // Swallow — analytics never blocks UX.
    }
  }
}

/**
 * ONCE_PER_SESSION dedup, persisted across page navigations.
 *
 * The in-memory `firedThisSession` Set above resets on every full page
 * load, so a "once per session" event actually re-fired once per *page* —
 * which is how marketing_opt_in reached ~2.3 hits/user across a multi-page
 * session. We persist the fired set in sessionStorage, keyed by the active
 * GA session id, so the guarantee survives navigations within the session
 * and still resets cleanly when GA4 rolls a new session (the key changes).
 *
 * Falls back to in-memory-only when sessionStorage is unavailable
 * (privacy mode, blocked storage) — never throws into the booking flow.
 */
function dedupStorageKey(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    if (!window.sessionStorage) return null;
  } catch {
    return null;
  }
  return `cc_widget_fired:${getSessionId()}`;
}

function hasFiredThisSession(eventName: string): boolean {
  if (firedThisSession.has(eventName)) return true;
  const key = dedupStorageKey();
  if (!key) return false;
  try {
    const raw = window.sessionStorage.getItem(key);
    return raw ? raw.split(',').includes(eventName) : false;
  } catch {
    return false;
  }
}

function markFiredThisSession(eventName: string): void {
  firedThisSession.add(eventName);
  const key = dedupStorageKey();
  if (!key) return;
  try {
    const raw = window.sessionStorage.getItem(key);
    const fired = new Set(raw ? raw.split(',') : []);
    fired.add(eventName);
    window.sessionStorage.setItem(key, Array.from(fired).join(','));
  } catch {
    // sessionStorage write failed — in-memory Set still guards this page.
  }
}

/**
 * Fires a GrowthBook experiment exposure through the widget's existing
 * dispatch pipeline so it lands in GA4 → BigQuery next to funnel events,
 * joined back to the session via web_submission_id. Called from GrowthBook's
 * trackingCallback (see src/main.tsx) — which the SDK invokes once per
 * experiment per instance, so no extra dedup is applied here.
 *
 * Fire and forget, like the rest of this module: an analytics failure must
 * never block the booking flow.
 */
export function dispatchExposure(
  experimentKey: string,
  variantId: string | number,
  variantValue: unknown,
): void {
  if (typeof window === 'undefined') return;
  try {
    const params = {
      experiment_key: experimentKey,
      variant_id: String(variantId),
      variant_value:
        typeof variantValue === 'string' ? variantValue : JSON.stringify(variantValue),
      web_submission_id: getWebSubmissionId(),
      growthbook_visitor_id: getGrowthBookVisitorId(),
    };
    window.dataLayer?.push({ event: 'cc_experiment_viewed', ...params });
    postToSgtm('cc_experiment_viewed', params);
  } catch {
    // Exposure tracking must never break the widget.
  }
}

/**
 * Public emit function. Auto-injects schema_version, user_id (if known),
 * channelmix_client_id (if present), and the per-session web_submission_id.
 *
 * Honors the ONCE_PER_SESSION list — events in that set fire at most once
 * per GA4 session, deduped across page navigations via sessionStorage.
 */
export function emitAnalytics(payload: EventParams): void {
  try {
    emitAnalyticsInner(payload);
  } catch {
    /* analytics never blocks the booking flow or the widget UI */
  }
}

function emitAnalyticsInner(payload: EventParams): void {
  const eventName = payload.event;

  if (ONCE_PER_SESSION.has(eventName)) {
    if (hasFiredThisSession(eventName)) return;
    markFiredThisSession(eventName);
  }

  // Spread caller params, then layer auto-injected fields. Caller can
  // override auto-injected values if they explicitly pass them.
  //
  // CRITICAL: we used to spread getAttribution() into every event's
  // parameters here. That sent `utm_source`/`utm_medium`/`utm_campaign`/
  // `gclid` as event-level parameters on every emit. GA4 interprets
  // those reserved names as MANUAL TRAFFIC SOURCE OVERRIDES, and when
  // a user has no UTMs on their landing URL (most direct traffic) they
  // arrive as null/empty — which forces traffic_source to (not set) on
  // that event, overriding GA4's correctly-resolved session-level
  // source. The legacy events bypass emitAnalytics so they kept their
  // session-level attribution; the new-schema events (book_flow_*,
  // channelmix_conversion) inherited the override and landed in
  // Unassigned at >85% rates because of it. Session source is the
  // source of truth; we don't need to re-state it per event.
  const enriched: Record<string, unknown> = {
    schema_version: SCHEMA_VERSION,
    web_submission_id: getWebSubmissionId(),
    channelmix_client_id: getChannelMixClientId(),
    // Only include user_id when actually set. Previous shape was
    // `user_id: persistentUserId ?? undefined`, which kept the key
    // present with a literal `undefined` value — harmless on the
    // direct sGTM path (postToSgtm filters undefined before
    // serializing) but poisonous on the Web GTM dataLayer path,
    // where downstream tags could forward `user_id="undefined"`
    // as a real string and merge anonymous users into a single
    // phantom identity in GA4.
    ...(persistentUserId ? { user_id: persistentUserId } : {}),
    ...(payload as unknown as Record<string, unknown>),
  };

  // dataLayer push uses the standard GTM `event` key. We strip our
  // typed `event` discriminant when posting to sGTM since it's used as
  // the GA4 event name in `en=...`.
  pushToDataLayer({ ...enriched });

  // For sGTM, the `event` key on the payload becomes the GA4 event name
  // (already used as `en=` query param). Don't send it twice.
  const { event: _event, ...sgtmParams } = enriched as Record<string, unknown>;
  postToSgtm(eventName, sgtmParams);

  // Optional legacy `booking` schema dual-fire, gated on host-page config.
  // No-op unless window.CC_WIDGET_CONFIG.legacyDualFire is true.
  maybeEmitLegacy(payload);

  // dataLayer bridge for the Web Container's Facebook Pixel + Trade Desk
  // tags. Pushes destination-namespaced `cc_widget_*` events so only the
  // tags we explicitly re-trigger fire. Gated on hostPixelDispatch
  // (default enabled).
  maybeEmitHostPixels(payload);
}
